import { useLocation } from 'react-router-dom';
import { usePermissions } from './usePermissions';
import { ROUTE_PERMISSIONS, ROUTES } from '../config/permissions';

/**
 * Convert a path pattern (e.g. /step-study/:id) to a regex that matches pathnames.
 */
function patternToRegex(pattern) {
  const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const withParams = escaped.replace(/:[^/]+/g, '[^/]+');
  return new RegExp(`^${withParams}$`);
}

/**
 * Find the required permission for a pathname by matching against ROUTE_PERMISSIONS.
 * Exact match first, then dynamic patterns (longer patterns first for specificity).
 */
function getRequiredPermission(pathname) {
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;

  // Exact match
  if (ROUTE_PERMISSIONS[path] !== undefined) {
    return ROUTE_PERMISSIONS[path];
  }

  // Dynamic patterns: sort by path length descending so more specific matches first
  const patterns = Object.keys(ROUTE_PERMISSIONS).filter((p) => p.includes(':'));
  patterns.sort((a, b) => b.length - a.length);

  for (const pattern of patterns) {
    if (patternToRegex(pattern).test(path)) {
      return ROUTE_PERMISSIONS[pattern];
    }
  }

  return undefined;
}

/**
 * Find the ROUTES entry for a pathname (exact match, then longest dynamic pattern).
 * Used for document title and similar metadata.
 */
export function findRouteForPathname(pathname) {
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const exact = ROUTES.find((r) => r.path === path);
  if (exact) return exact;
  const patterns = ROUTES.filter((p) => p.path.includes(':')).sort((a, b) => b.path.length - a.path.length);
  for (const r of patterns) {
    if (patternToRegex(r.path).test(path)) return r;
  }
  return null;
}

/**
 * Returns whether the current route is allowed for the user.
 * Handles all dynamic segments (e.g. /step-study/:id, /ministry-checkup/:id/take).
 */
export function useRoutePermission() {
  const { pathname } = useLocation();
  const { hasPermission } = usePermissions();

  const requiredPermission = getRequiredPermission(pathname);
  const allowed = hasPermission(requiredPermission);

  return { allowed, requiredPermission };
}
